import { useCheckRoutingInfoQueryCache, useGetRoutingInfo } from "../shared/useQueryRoutingInfo"
import { carrotClient } from "#/utils/CarrotClient"
import {
  getAmabAfabMedicalPreferenceFromParam,
  getWorkflowFromParam
} from "#/components/carrot-plans/hooks/useMedicalQuestionMenuItemWorkflow"
import { useParams } from "react-router-dom"
import { AmabAfabMedicalPreference, MedicalQuestionsWorkflow } from "../steps/medical-questions/medicalFlowConstants"
import { useFlags } from "launchdarkly-react-client-sdk"
import { RoutingInfo } from "#/types/routingInfo"

function getWorkflowFromRouting(
  routingData: RoutingInfo | undefined,
  pregnancyAndPostpartumUpdatedRouting: boolean
): MedicalQuestionsWorkflow {
  const journey = routingData?.journey
  const medicalPreference = routingData?.amabAfabMedicalPreference
  const preservationInterest = routingData?.preservationInterest
  const journeyStage = routingData?.journeyStage

  // TODO: Remove "PREGNANT" from afabJourneys when removing LD flag above
  const afabJourneys = ["MENOPAUSE", "PREGNANT"]
  const medicalPreferenceJourneys = [
    "ASSISTED_REPRODUCTION",
    "PRESERVATION",
    "TRY_PREGNANT",
    "LOW_TESTOSTERONE",
    "EXPLORING"
  ]

  if (medicalPreferenceJourneys.includes(journey)) {
    // PRESERVATION with sperm/egg interest maps to AMAB/AFAB; otherwise fall through to AMAB/AFAB preference or decline.
    if (journey === "PRESERVATION") {
      if (preservationInterest === "PRESERVE_SPERM") {
        return MedicalQuestionsWorkflow.AMAB_MEDICAL_QUESTIONS
      } else if (preservationInterest === "PRESERVE_EGGS") {
        return MedicalQuestionsWorkflow.AFAB_MEDICAL_QUESTIONS
      }
    }
    if (medicalPreference === AmabAfabMedicalPreference.AFAB) {
      return MedicalQuestionsWorkflow.AFAB_MEDICAL_QUESTIONS
    }
    if (medicalPreference === AmabAfabMedicalPreference.AMAB) {
      return MedicalQuestionsWorkflow.AMAB_MEDICAL_QUESTIONS
    }
    return MedicalQuestionsWorkflow.DECLINE_MEDICAL_QUESTIONS
  }
  if (pregnancyAndPostpartumUpdatedRouting && journey === "PREGNANT") {
    return journeyStage === "POSTPARTUM"
      ? MedicalQuestionsWorkflow.POSTPARTUM_MEDICAL_QUESTIONS
      : MedicalQuestionsWorkflow.PREGNANCY_MEDICAL_QUESTIONS
  } else if (afabJourneys.includes(journey)) {
    return MedicalQuestionsWorkflow.AFAB_MEDICAL_QUESTIONS
  } else {
    return MedicalQuestionsWorkflow.DECLINE_MEDICAL_QUESTIONS
  }
}

export const useMedicalInfoWorkflow = (): {
  workflow: MedicalQuestionsWorkflow | undefined
  IsAMAB: boolean
  IsAFAB: boolean
  decline: boolean
} => {
  const { data: routingData } = useGetRoutingInfo()
  const { journey: journeyParam } = useParams<{ journey: string }>()
  const { pregnancyAndPostpartumUpdatedRouting } = useFlags()
  const workflow =
    journeyParam != null
      ? getWorkflowFromParam(journeyParam, pregnancyAndPostpartumUpdatedRouting)
      : getWorkflowFromRouting(routingData, pregnancyAndPostpartumUpdatedRouting)
  const IsAMAB = workflow === MedicalQuestionsWorkflow.AMAB_MEDICAL_QUESTIONS
  const IsAFAB = workflow === MedicalQuestionsWorkflow.AFAB_MEDICAL_QUESTIONS
  const decline = workflow === MedicalQuestionsWorkflow.DECLINE_MEDICAL_QUESTIONS
  return { workflow, IsAMAB, IsAFAB, decline }
}

export const useAmabAfabMedicalPreference = (): AmabAfabMedicalPreference | undefined => {
  const { journey: journeyParam } = useParams<{ journey: string }>()
  const routingInfo = useCheckRoutingInfoQueryCache()

  if (journeyParam != null) {
    return getAmabAfabMedicalPreferenceFromParam(journeyParam)
  }

  return routingInfo?.amabAfabMedicalPreference as AmabAfabMedicalPreference
}

export const useCreateZendeskTicket = () => {
  // @ts-expect-error TS(7006) FIXME: Parameter 'stepId' implicitly has an 'any' type.
  const createZendeskTicket = async (stepId) => {
    await carrotClient.createZendeskTicketForCPMedicalQuestions(stepId)
  }
  return { createZendeskTicket }
}
